import { createFileRoute, redirect } from "@tanstack/react-router";
import type { RouterContextWithSession } from "../../../router";
import { useState } from "react";
import { useAuth } from "../../../components/AuthProvider";
import { createRouteError } from "../../../components/RouteErrorBoundary";
import { PageSkeleton } from "../../../components/RouteSkeleton";
import { Button } from "../../../components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../../../components/ui/card";
import { Input } from "../../../components/ui/input";
import { Label } from "../../../components/ui/label";
import { Badge } from "../../../components/ui/badge";
import { Progress } from "../../../components/ui/progress";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../../../components/ui/tabs";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../../../components/ui/table";
import { toast } from "../../../components/ui/sonner";
import {
  Trophy,
  Flame,
  Zap,
  Target,
  Users,
  AlertTriangle,
  CheckCircle2,
  RefreshCw,
  Search,
  BarChart3,
  TrendingUp,
  Star,
} from "lucide-react";

export const Route = createFileRoute("/_admin/streaks/")({
  beforeLoad: async ({ context }) => {
    if (typeof window === "undefined") return {};
    const sb = context.supabase;
    if (!sb) return {};
    const {
      data: { session },
    } = await sb.auth.getSession();
    if (!session) {
      throw redirect({ to: "/auth" });
    }
    return { session };
  },
  loader: async ({ context }) => {
    if (typeof window === "undefined") return;
    const { session } = context as RouterContextWithSession;
    if (!session) return;
    // Session validated — component renders sample streak data.
    // TODO: Replace with real gamification API fetch in future phase.
  },
  pendingComponent: () => <PageSkeleton rows={4} variant="cards" />,
  errorComponent: createRouteError("Streak Manager"),
  component: StreakManagerPage,
});

// ── Types ─────────────────────────────────────────────────────────────────

interface UserStreak {
  id: string;
  name: string;
  email: string;
  currentStreak: number;
  longestStreak: number;
  totalXp: number;
  dailyXp: number;
  tasksCompleted: number;
  lastActiveDate: string;
}

// ── Sample Data ───────────────────────────────────────────────────────────

const SAMPLE_STREAKS: UserStreak[] = [
  {
    id: "1",
    name: "Alex Johnson",
    email: "alex@flowstate.io",
    currentStreak: 15,
    longestStreak: 42,
    totalXp: 12500,
    dailyXp: 350,
    tasksCompleted: 234,
    lastActiveDate: "2026-03-23",
  },
  {
    id: "2",
    name: "Sarah Williams",
    email: "sarah@flowstate.io",
    currentStreak: 8,
    longestStreak: 30,
    totalXp: 8900,
    dailyXp: 250,
    tasksCompleted: 178,
    lastActiveDate: "2026-03-23",
  },
  {
    id: "3",
    name: "Mike Chen",
    email: "mike@flowstate.io",
    currentStreak: 23,
    longestStreak: 23,
    totalXp: 15200,
    dailyXp: 450,
    tasksCompleted: 312,
    lastActiveDate: "2026-03-23",
  },
  {
    id: "4",
    name: "Emma Davis",
    email: "emma@flowstate.io",
    currentStreak: 0,
    longestStreak: 18,
    totalXp: 5600,
    dailyXp: 0,
    tasksCompleted: 89,
    lastActiveDate: "2026-03-15",
  },
  {
    id: "5",
    name: "James Wilson",
    email: "james@flowstate.io",
    currentStreak: 5,
    longestStreak: 12,
    totalXp: 3400,
    dailyXp: 180,
    tasksCompleted: 56,
    lastActiveDate: "2026-03-23",
  },
];

// ── Main Page ─────────────────────────────────────────────────────────────

function StreakManagerPage() {
  const { loading, isAuthenticated } = useAuth();

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <p className="text-muted-foreground">Loading…</p>
      </div>
    );
  }

  if (!isAuthenticated) {
    return (
      <div className="flex items-center justify-center py-12">
        <p className="text-muted-foreground">Redirecting to sign-in…</p>
      </div>
    );
  }

  return <AuthenticatedStreakManager />;
}

// ── Authenticated sub-component ───────────────────────────────────────────

function AuthenticatedStreakManager() {
  const [streaks, setStreaks] = useState<UserStreak[]>(SAMPLE_STREAKS);
  const [isProcessing, setIsProcessing] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [individualUserId, setIndividualUserId] = useState("");
  const [processResults, setProcessResults] = useState<string | null>(null);

  // Stats
  const avgStreak = Math.round(
    streaks.reduce((sum, s) => sum + s.currentStreak, 0) / streaks.length
  );
  const maxStreak = Math.max(...streaks.map((s) => s.longestStreak));
  const totalXp = streaks.reduce((sum, s) => sum + s.totalXp, 0);
  const activeUsers = streaks.filter((s) => s.currentStreak > 0).length;

  const filteredStreaks = streaks.filter(
    (s) =>
      !searchQuery ||
      s.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      s.email.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const handleBackdateAll = async () => {
    setIsProcessing(true);
    setProcessResults(null);
    // Simulate processing
    await new Promise((r) => setTimeout(r, 2000));
    setIsProcessing(false);
    setProcessResults(
      `Successfully recalculated streaks for ${streaks.length} users from historical task data.`
    );
    toast.success("Streak backdate completed");
  };

  const handleIndividualFix = async () => {
    if (!individualUserId.trim()) {
      toast.error("Please enter a user ID");
      return;
    }
    setIsProcessing(true);
    setProcessResults(null);
    await new Promise((r) => setTimeout(r, 1000));
    setIsProcessing(false);
    setProcessResults(
      `Recalculated streak for user ${individualUserId}. Current streak and XP updated.`
    );
    toast.success("Individual streak fix completed");
    setIndividualUserId("");
  };

  const handlePointsReset = async () => {
    setIsProcessing(true);
    setProcessResults(null);
    await new Promise((r) => setTimeout(r, 2000));
    setStreaks((prev) =>
      prev.map((s) => ({
        ...s,
        totalXp: Math.round(s.tasksCompleted * 50),
        dailyXp: 0,
      }))
    );
    setIsProcessing(false);
    setProcessResults(
      `Points reset completed for ${streaks.length} users. XP recalculated based on task completion (50 XP/task).`
    );
    toast.success("Points reset completed");
  };

  // XP threshold: if a user's total XP exceeds this multiplier per task,
  // they may be affected by the subtask achievement bonus bug.
  const XP_ANOMALY_THRESHOLD_PER_TASK = 60;

  const handleBugAudit = async () => {
    setIsProcessing(true);
    setProcessResults(null);
    await new Promise((r) => setTimeout(r, 1500));
    setIsProcessing(false);
    const affected = streaks.filter((s) => s.totalXp > s.tasksCompleted * XP_ANOMALY_THRESHOLD_PER_TASK);
    setProcessResults(
      affected.length > 0
        ? `Found ${affected.length} users potentially affected by the subtask achievement bonus bug: ${affected.map((u) => u.name).join(", ")}`
        : "No users found affected by the subtask achievement bonus bug. All XP values appear correct."
    );
    toast.success("Bug audit completed");
  };

  const formatXp = (xp: number) => {
    return xp >= 1000 ? `${(xp / 1000).toFixed(1)}k` : String(xp);
  };

  return (
    <main className="container mx-auto p-6 space-y-6 max-w-5xl">
      {/* Header */}
      <div>
        <h1 className="text-3xl font-bold">Streak Manager</h1>
        <p className="text-muted-foreground mt-1">
          Manage user streaks, XP, and gamification data.
        </p>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-muted-foreground">
                  Avg Streak
                </p>
                <p className="text-2xl font-bold">{avgStreak} days</p>
              </div>
              <Flame className="h-5 w-5 text-orange-500" />
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-muted-foreground">
                  Max Streak
                </p>
                <p className="text-2xl font-bold">{maxStreak} days</p>
              </div>
              <Trophy className="h-5 w-5 text-yellow-500" />
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-muted-foreground">
                  Total XP
                </p>
                <p className="text-2xl font-bold">{formatXp(totalXp)}</p>
              </div>
              <Zap className="h-5 w-5 text-purple-500" />
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-muted-foreground">
                  Active Users
                </p>
                <p className="text-2xl font-bold">
                  {activeUsers}/{streaks.length}
                </p>
              </div>
              <Users className="h-5 w-5 text-green-500" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Tabs */}
      <Tabs defaultValue="stats" className="space-y-4">
        <TabsList>
          <TabsTrigger value="stats">
            <BarChart3 className="mr-2 h-4 w-4" />
            Current Stats
          </TabsTrigger>
          <TabsTrigger value="streak-fix">
            <RefreshCw className="mr-2 h-4 w-4" />
            Streak Fix
          </TabsTrigger>
          <TabsTrigger value="individual">
            <Target className="mr-2 h-4 w-4" />
            Individual Fix
          </TabsTrigger>
          <TabsTrigger value="points">
            <Star className="mr-2 h-4 w-4" />
            Points Reset
          </TabsTrigger>
          <TabsTrigger value="audit">
            <AlertTriangle className="mr-2 h-4 w-4" />
            Bug Audit
          </TabsTrigger>
        </TabsList>

        {/* Current Stats Tab */}
        <TabsContent value="stats" className="space-y-4">
          <div className="flex gap-3 items-center">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Search users..."
                className="pl-10"
              />
            </div>
          </div>

          <Card>
            <CardContent className="p-0">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>User</TableHead>
                    <TableHead>Current Streak</TableHead>
                    <TableHead>Longest Streak</TableHead>
                    <TableHead>Total XP</TableHead>
                    <TableHead>Tasks</TableHead>
                    <TableHead>Last Active</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredStreaks.map((streak) => (
                    <TableRow key={streak.id}>
                      <TableCell>
                        <div>
                          <div className="font-medium text-sm">
                            {streak.name}
                          </div>
                          <div className="text-xs text-muted-foreground">
                            {streak.email}
                          </div>
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          <Flame
                            className={`h-4 w-4 ${
                              streak.currentStreak > 0
                                ? "text-orange-500"
                                : "text-muted-foreground"
                            }`}
                          />
                          <span className="font-medium">
                            {streak.currentStreak}
                          </span>
                          <span className="text-xs text-muted-foreground">
                            days
                          </span>
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          <Trophy className="h-4 w-4 text-yellow-500" />
                          <span>{streak.longestStreak} days</span>
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          <Zap className="h-4 w-4 text-purple-500" />
                          <span>{formatXp(streak.totalXp)}</span>
                          {streak.dailyXp > 0 && (
                            <Badge variant="secondary" className="text-xs">
                              +{streak.dailyXp} today
                            </Badge>
                          )}
                        </div>
                      </TableCell>
                      <TableCell>{streak.tasksCompleted}</TableCell>
                      <TableCell className="text-sm text-muted-foreground">
                        {streak.lastActiveDate}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Streak Fix Tab */}
        <TabsContent value="streak-fix" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <RefreshCw className="h-5 w-5" />
                Backdate All User Streaks
              </CardTitle>
              <CardDescription>
                Recalculate all user streaks from historical task completion
                data. This will scan each user&apos;s task history and rebuild
                their streak information.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="rounded-lg border border-yellow-200 bg-yellow-50 dark:bg-yellow-950/20 dark:border-yellow-800 p-4">
                <div className="flex items-start gap-2">
                  <AlertTriangle className="h-4 w-4 text-yellow-600 mt-0.5" />
                  <div className="text-sm text-yellow-800 dark:text-yellow-200">
                    <p className="font-medium">Caution</p>
                    <p>
                      This operation will overwrite current streak data for all
                      users. Make sure you have a backup before proceeding.
                    </p>
                  </div>
                </div>
              </div>
              <Button
                onClick={handleBackdateAll}
                disabled={isProcessing}
              >
                {isProcessing ? (
                  <RefreshCw className="mr-2 h-4 w-4 animate-spin" />
                ) : (
                  <RefreshCw className="mr-2 h-4 w-4" />
                )}
                {isProcessing ? "Processing..." : "Backdate All Streaks"}
              </Button>
              {processResults && (
                <div className="rounded-lg border bg-muted/30 p-4 text-sm">
                  <CheckCircle2 className="h-4 w-4 text-green-500 inline mr-2" />
                  {processResults}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Individual Fix Tab */}
        <TabsContent value="individual" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Target className="h-5 w-5" />
                Fix Individual User Streak
              </CardTitle>
              <CardDescription>
                Recalculate a specific user&apos;s streak by their user ID.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <Label htmlFor="user-id">User ID</Label>
                <Input
                  id="user-id"
                  value={individualUserId}
                  onChange={(e) => setIndividualUserId(e.target.value)}
                  placeholder="Enter user ID (UUID)"
                />
              </div>
              <Button
                onClick={handleIndividualFix}
                disabled={isProcessing || !individualUserId.trim()}
              >
                {isProcessing ? (
                  <RefreshCw className="mr-2 h-4 w-4 animate-spin" />
                ) : (
                  <Target className="mr-2 h-4 w-4" />
                )}
                {isProcessing ? "Processing..." : "Recalculate Streak"}
              </Button>
              {processResults && (
                <div className="rounded-lg border bg-muted/30 p-4 text-sm">
                  <CheckCircle2 className="h-4 w-4 text-green-500 inline mr-2" />
                  {processResults}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Points Reset Tab */}
        <TabsContent value="points" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Star className="h-5 w-5" />
                Reset All User Points
              </CardTitle>
              <CardDescription>
                Reset all user XP points based on updated rules. This fixes the
                subtask achievement bonus bug that was giving extra XP.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="rounded-lg border border-red-200 bg-red-50 dark:bg-red-950/20 dark:border-red-800 p-4">
                <div className="flex items-start gap-2">
                  <AlertTriangle className="h-4 w-4 text-red-600 mt-0.5" />
                  <div className="text-sm text-red-800 dark:text-red-200">
                    <p className="font-medium">Destructive Operation</p>
                    <p>
                      This will permanently recalculate all user points. Current
                      XP values will be replaced with the corrected amounts (50
                      XP per completed task).
                    </p>
                  </div>
                </div>
              </div>
              <Button
                variant="destructive"
                onClick={handlePointsReset}
                disabled={isProcessing}
              >
                {isProcessing ? (
                  <RefreshCw className="mr-2 h-4 w-4 animate-spin" />
                ) : (
                  <Star className="mr-2 h-4 w-4" />
                )}
                {isProcessing ? "Processing..." : "Reset All Points"}
              </Button>
              {processResults && (
                <div className="rounded-lg border bg-muted/30 p-4 text-sm">
                  <CheckCircle2 className="h-4 w-4 text-green-500 inline mr-2" />
                  {processResults}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Bug Audit Tab */}
        <TabsContent value="audit" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <AlertTriangle className="h-5 w-5" />
                Subtask Achievement Bug Audit
              </CardTitle>
              <CardDescription>
                Audit users to identify those affected by the subtask
                achievement bonus bug. This scans XP values against expected
                amounts to find anomalies.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <Button
                variant="outline"
                onClick={handleBugAudit}
                disabled={isProcessing}
              >
                {isProcessing ? (
                  <RefreshCw className="mr-2 h-4 w-4 animate-spin" />
                ) : (
                  <Search className="mr-2 h-4 w-4" />
                )}
                {isProcessing ? "Auditing..." : "Run Bug Audit"}
              </Button>
              {processResults && (
                <div className="rounded-lg border bg-muted/30 p-4 text-sm">
                  <CheckCircle2 className="h-4 w-4 text-green-500 inline mr-2" />
                  {processResults}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </main>
  );
}
